import { useEffect, useState } from "react";

function parseCSV(csv: string): Record<string, string>[] {
  const lines = csv.trim().split("\n");
  if (lines.length < 2) return [];

  const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, ""));

  return lines.slice(1).map((line) => {
    const values = line.split("\",\"").map((v) => v.trim().replace(/^"|"$/g, "").replace(/""/g, "\""));  // eslint-disable-line quotes
    const obj: Record<string, string> = {};
    headers.forEach((header, idx) => {
      obj[header] = values[idx] ?? "";
    });
    return obj;
  });
}

interface UseGoogleCalcTableReturn {
  data: Record<string, string>[] | null;
  loading: boolean;
  error: string | null;
}

/**
 * Hook to fetch and parse data from a Google Sheets document as CSV
 *
 * @param sheetId - The Google Sheets document ID
 * @param gid - The sheet GID (defaults to first sheet if empty)
 * @returns Object with data, loading state, and error
 *
 * @example
 * const { data, loading, error } = useGoogleCalcTable('1abc123...', '0');
 *
 * @note For Google Drive images: https://drive.google.com/thumbnail?id={driveImageId}
 */
const useGoogleCalcTable = (sheetId: string, gid = ""): UseGoogleCalcTableReturn => {
  const [data, setData] = useState<Record<string, string>[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    fetch(`https://docs.google.com/spreadsheets/d/${sheetId}/gviz/tq?tqx=out:csv&gid=${gid}`)
      .then((res) => {
        if (!res.ok) throw new Error("Network response was not ok");
        return res.text();
      })
      .then((csv) => {
        setData(parseCSV(csv));
        setLoading(false);
      })
      .catch((err: Error) => {
        setError(err.message);
        setLoading(false);
      });
  }, [sheetId, gid]);

  return { data, loading, error };
};

export default useGoogleCalcTable;
